/* Overview · what needs you today. Design: ConsoleOverview.dc.html. */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFleet } from '../data/FleetData';
import { notLoggingBuses, thirstPct, thirstyBuses, urgentOf } from '../data/urgent';
import { usePage, useShell } from '../Shell';
import { useTakeover } from '../zana/Takeover';
import { fuelCheckPlan, offRoadPlan, papersWhy, Plan, remindPlan } from '../zana/plans';
import { ExplainPoint, StatQ } from '../zana/Explain';
import MapView from '../MapView';
import { notifyBusDriver } from '../../services/briefingsApi';
import {
  Chip, Empty, IcArrow, PageBody, ShimRow, Shim, ShimHead, ShimStats, ShimTable, Spark, StaleBanner, StatusChip, ZanaMark,
  hhmm, n0, plateBeside, plural, useAction, useCountUp, useGo,
} from '../kit';

const DAY = 86400000;

interface Todo {
  id: string;
  sev: 'bad' | 'warn' | 'faint';
  when: 'Today' | 'This week' | 'When you can';
  zana?: boolean;
  title: string;
  body: string;
  actions: React.ReactNode;
  /** Zana can do this one herself, after showing her plan. */
  plan?: Plan | null;
}

const Skeleton: React.FC = () => (
  <div className="wrap">
    <ShimHead />
    <div className="two" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1fr)', gap: 24 }}>
      <div className="panel">{[0, 1, 2, 3].map((i) => <ShimRow key={i} />)}</div>
      <div className="panel" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Shim w="100%" h={200} /><Shim w="70%" h={14} /><Shim w="90%" h={14} />
      </div>
    </div>
    <ShimStats />
    <ShimTable />
  </div>
);

/** Weekly totals for the last 7 weeks, oldest first (the stat tile's trend). */
function weekly(points: { at: Date | null; v: number }[]): number[] {
  const now = Date.now();
  const out = Array(7).fill(0);
  points.forEach((p) => {
    if (!p.at) return;
    const w = Math.floor((now - p.at.getTime()) / (7 * DAY));
    if (w >= 0 && w < 7) out[6 - w] += p.v;
  });
  return out;
}

const Overview: React.FC = () => {
  const f = useFleet();
  const { ask, dark, openAddBus } = useShell();
  const take = useTakeover();
  const tctx = take?.ctx;
  const go = useGo();
  const navigate = useNavigate();
  const action = useAction();
  const [done, setDone] = useState<Set<string>>(new Set());

  const hour = new Date().getHours();
  const part = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';

  const onRoad = f.buses.filter((b) => b.status === 'on_route' || b.status === 'late');
  const aboard = onRoad.reduce((s, b) => s + (b.aboard || 0), 0);
  const expectedOnRoad = onRoad.reduce((s, b) => s + (b.expected || 0), 0);
  const late = f.buses.filter((b) => b.status === 'late');

  /* The Urgent list (data/urgent), shared with the Alerts page's Urgent tab. */
  const urgent = useMemo(() => urgentOf(f), [f]);
  const thirstiest = useMemo(() => thirstyBuses(f)[0] || null, [f]);
  const quiet = useMemo(() => notLoggingBuses(f), [f]);

  /* To do — most urgent first, built from the records. */
  const todos = useMemo<Todo[]>(() => {
    const list: Todo[] = [];
    const now = Date.now();
    f.buses.forEach((b) => {
      const expired = b.papers.filter((p) => p.kind !== 'service' && p.due && p.due.getTime() < now);
      if (expired.length && b.status !== 'off_road') {
        const days = Math.max(1, Math.round((now - Math.max(...expired.map((p) => (p.due as Date).getTime()))) / DAY));
        list.push({
          id: `papers-${b.id}`, sev: 'bad', when: 'Today',
          title: `Take ${b.plate} off the road`,
          body: `${expired.map((p) => p.label).join(' and ')} ${expired.length > 1 ? 'both expired' : 'expired'} ${days === 1 ? 'yesterday' : `${days} days ago`}.${b.tracking ? ' It is on a run right now.' : ''}`,
          actions: <a className="btn s b-ink" {...go('ConsoleMaintenance')}>Renew or book</a>,
          plan: tctx ? offRoadPlan(tctx, b, papersWhy(b) || 'papers expired') : null,
        });
      }
    });
    const listed = new Set(list.map((t) => t.id.replace('papers-', '')));
    f.alerts.filter((a) => !a.read && a.severity === 'urgent' && !(a.busId && listed.has(a.busId))).slice(0, 2).forEach((a) => list.push({
      id: `alert-${a.id}`, sev: 'bad', when: 'Today', title: a.title, body: a.body,
      actions: <a className="btn s b-ink" {...go('ConsoleAlerts')}>Open alert</a>,
    }));
    if (thirstiest) {
      const b = thirstiest;
      const pct = thirstPct(b);
      list.push({
        id: `fuel-${b.id}`, sev: 'warn', when: 'This week', zana: true,
        title: `${b.plate} is using ${pct}% more fuel than its normal`,
        body: `${b.kmpl?.toFixed(1)} km per litre against its own ${b.kmplNormal?.toFixed(1)} over the last 30 days.`,
        actions: <>
          <button type="button" className="btn s b-ghost" style={{ color: 'var(--z-ink)', position: 'relative', zIndex: 2 }} onClick={() => ask(`Why is ${b.plate} burning more fuel?`, b.plate)}>Why?</button>
          <a className="btn s b-line" {...go('ConsoleFuel')}>See fuel</a>
        </>,
        plan: tctx ? fuelCheckPlan(tctx, b) : null,
      });
    }
    if (f.childrenUnlinked > 0) list.push({
      id: 'unlinked', sev: 'warn', when: 'This week',
      title: `${plural(f.childrenUnlinked, 'child is', 'children are')} not linked to a current parent`,
      body: 'Their parents will not get pickup alerts until each child has a current parent.',
      actions: <a className="btn s b-line" {...go('ConsolePeople', '?tab=parents')}>Fix roster</a>,
    });
    if (quiet.length) list.push({
      id: 'quiet', sev: 'faint', when: 'When you can',
      title: `${plural(quiet.length, 'driver has', 'drivers have')} not logged a refuel in 7 days`,
      body: 'Fuel checks for those buses are estimates until they log the next fill.',
      actions: <button type="button" className="btn s b-line" onClick={() => {
        void action({
          busy: `Sending reminders to ${plural(quiet.length, 'driver')}…`,
          done: (n: number) => `Reminder sent to ${plural(n, 'driver')}`,
          fail: 'Couldn’t send the reminders. Try again in a minute.',
          run: async () => {
            const sent = await Promise.all(quiet.map((b) => notifyBusDriver(f.org.code, b.id, 'Please log your last refuel in the Trasse app.').then((r) => (r.ok ? 1 : 0)).catch(() => 0)));
            const n = sent.reduce<number>((s, x) => s + x, 0);
            if (!n) throw new Error('none sent');
            setDone((d) => new Set(d).add('quiet'));
            return n;
          },
        });
      }}>Remind drivers</button>,
      plan: tctx ? remindPlan(tctx, quiet) : null,
    });
    return list;
  }, [f.buses, f.alerts, f.childrenUnlinked, f.org.code, thirstiest, quiet, go, ask, action, tctx]);

  const visible = todos.filter((t) => !done.has(t.id));
  const urgentCount = visible.filter((t) => t.when !== 'When you can').length;

  /* Stats: one number, its change, a 7-point trend and what it is compared with. */
  const now = Date.now();
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime();
  const prevMonthStart = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).getTime();
  const dayOfMonth = new Date().getDate();
  /* Fuel this month: the cost of each refill drivers logged since the 1st (litres
     × the pump price), against the same days of last month — clamped so 30 March
     compares with all of February, not with the start of March. */
  const prevDays = Math.min(dayOfMonth, Math.round((monthStart - prevMonthStart) / DAY));
  const prevName = new Date(prevMonthStart).toLocaleDateString('en-GB', { month: 'short' });
  const fuelMonth = f.refills.filter((r) => r.at && r.at.getTime() >= monthStart).reduce((s, r) => s + (r.amount || 0), 0);
  const fuelPrevSame = f.refills.filter((r) => r.at && r.at.getTime() >= prevMonthStart && r.at.getTime() < prevMonthStart + prevDays * DAY).reduce((s, r) => s + (r.amount || 0), 0);
  /* A bus that stopped logging makes the month look cheaper than it was, so the
     change is only called good or bad when every bus is logging. */
  const notLogging = quiet.length;
  const runsDone = f.runs.filter((r) => r.status !== 'cancelled' && r.type !== 'refuel');
  const carriedMonth = runsDone.filter((r) => r.date.getTime() >= monthStart).reduce((s, r) => s + r.served, 0);
  const km30 = f.buses.reduce((s, b) => s + (b.km30 || 0), 0);
  const km7 = runsDone.filter((r) => now - r.date.getTime() < 7 * DAY).reduce((s, r) => s + (r.km || 0), 0);
  const kmPrev7 = runsDone.filter((r) => now - r.date.getTime() >= 7 * DAY && now - r.date.getTime() < 14 * DAY).reduce((s, r) => s + (r.km || 0), 0);
  const fuelAnim = useCountUp(fuelMonth);
  const pct = (a: number, b: number) => (b > 0 ? Math.round(((a - b) / b) * 100) : null);
  const fuelDelta = pct(fuelMonth, fuelPrevSame);
  const kmDelta = pct(km7, kmPrev7);
  // Incident alerts are counted in Analytics → Incidents, not here.
  const unread = f.alerts.filter((a) => !a.read && a.kind !== 'incident');

  /* What Zana explains on this page — and the ? on the stats — worded from
     the same figures the page shows. */
  const pt = useMemo(() => {
    const km: ExplainPoint = {
      key: 'ov-km', label: 'Kilometres, last 7 days', mark: 'last 7 days',
      short: `${n0(km7)} km driven by your buses in the last 7 days${kmDelta != null ? `, ${kmDelta >= 0 ? 'up' : 'down'} ${Math.abs(kmDelta)}% on the week before` : ''}.`,
      means: 'Added up from the distance of each finished run. A run with tracking off adds nothing, so its bus reads low.',
      matters: 'Kilometres decide fuel, tyre wear and when each bus is due a service.',
      follow: ['Which bus drove the most this week?'],
    };
    const fuel: ExplainPoint = {
      key: 'ov-fuel', label: 'Fuel this month', mark: 'drivers logged',
      short: `The cost of the refills drivers logged since the 1st: Ksh ${n0(fuelMonth)}${fuelDelta != null ? `, ${fuelDelta > 0 ? 'up' : 'down'} ${Math.abs(fuelDelta)}% on the same days of ${prevName}` : ''}.`,
      means: 'Each refill a driver logs in the Trasse app, with what it cost. A fill nobody logs is not counted.',
      matters: 'Fuel is a fleet’s biggest running cost after salaries.',
      watch: notLogging > 0 ? `${plural(notLogging, 'bus has', 'buses have')} not logged a refill in 7 days, so the real figure may be higher.` : 'Every bus that drove has logged a refill this week, so this figure is complete.',
      follow: ['Which buses use the most fuel?', ...(notLogging > 0 ? ['Who isn’t logging refills?'] : [])],
      open: { page: 'ConsoleFuel', label: 'Open fuel' },
    };
    const kids: ExplainPoint = {
      key: 'ov-kids', label: 'Children carried', mark: 'counts twice',
      short: `${n0(carriedMonth)} pickups and drop-offs this month. A child on the morning and the evening run counts twice.`,
      means: 'Each child a driver marked picked up or dropped off on a finished run.',
      matters: 'It shows how full your buses are, which decides when a route needs a bigger bus.',
      follow: ['Which buses are fullest?'],
    };
    const al: ExplainPoint = {
      key: 'ov-alerts', label: 'Open alerts', mark: 'urgent',
      short: `${plural(unread.length, 'alert')} not read yet${urgent.count ? `, and ${urgent.count} urgent` : ', none urgent'}.`,
      means: 'Urgent holds expired papers and overdue services, buses off the road, fuel alerts and buses using a quarter more fuel than their normal.',
      follow: ['Which alerts need me today?'],
      open: { page: 'ConsoleAlerts', label: 'Open alerts' },
    };
    const list: ExplainPoint[] = [{
      key: 'ov-todo', label: 'To do', mark: 'most urgent first',
      short: visible.length ? `${plural(visible.length, 'thing')} for you, most urgent first.` : 'Nothing needs you right now.',
      means: 'Built from your records as they change: expired papers, fuel above a bus’s normal, urgent alerts, children without a current parent and drivers not logging refills. Where you see “Zana can do it”, I can take the job over.',
    }, km, fuel, kids, al, {
      key: 'ov-buses', label: 'Buses right now', mark: 'on the road',
      short: `${onRoad.length} of ${plural(f.buses.length, 'bus', 'buses')} are on the road right now.`,
      means: 'One row per bus: where it is in its run, how many children are aboard, and the one thing that needs a look.',
      open: { page: 'ConsoleBuses', label: 'All buses' },
    }];
    return { km, fuel, kids, al, list };
  }, [km7, kmDelta, fuelMonth, fuelDelta, prevName, notLogging, carriedMonth, unread.length, urgent.count, visible.length, onRoad.length, f.buses.length]);
  const points = f.buses.length ? pt.list : [];
  usePage({
    key: 'ConsoleOverview', title: 'Overview', zanaContext: 'Overview · today',
    zanaIntro: 'Ask about anything on this page. I can explain a number, find a child or a bus, or draft a note to parents.',
    prompts: ['Which buses need a look today?', 'Which children were not picked up today?', 'Summarise this week for the head teacher'],
    points,
    /* The same figures the cards show, so a Why answer cannot invent a different percent. */
    zana: {
      cards: [
        { label: 'Fuel this month', value: `Ksh ${n0(fuelMonth)}` },
        { label: 'Kilometres last 7 days', value: `${n0(km7)} km` },
        { label: 'Children carried', value: n0(carriedMonth) },
        { label: 'Open alerts', value: String(unread.length) },
        ...(thirstiest && thirstiest.kmpl != null && thirstiest.kmplNormal != null ? [{
          label: `${thirstiest.plate} fuel vs its normal`,
          value: `${thirstPct(thirstiest)}% more, ${thirstiest.kmpl.toFixed(1)} km per litre against ${thirstiest.kmplNormal.toFixed(1)}`,
        }] : []),
      ],
      focus: thirstiest ? [thirstiest.plate.replace(/[\s-]/g, '').toUpperCase()] : [],
    },
  });

  const greetName = f.me.name.split(/\s+/)[0];
  const headline = visible.length === 0
    ? `Good ${part}, ${greetName}. Nothing needs you right now.`
    : `Good ${part}, ${greetName}. ${urgentCount === 0 ? 'A few things when you can.' : `${urgentCount === 1 ? 'One thing needs' : `${['Two', 'Three', 'Four', 'Five'][urgentCount - 2] || urgentCount} things need`} you today.`}`;
  const subline = f.buses.length === 0
    ? 'Add your first bus and its driver to see it here.'
    : `${late.length ? `${plural(late.length, 'bus is', 'buses are')} delayed. ` : 'Everything else is on time. '}${onRoad.length} of ${f.buses.length} buses are on the road${onRoad.length ? ` with ${n0(aboard)} children` : ''}.`;
  const dateLine = `${new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })} · ${part === 'morning' ? 'morning runs' : part === 'afternoon' ? 'afternoon runs' : 'evening'}`;

  return (
    <PageBody loading={f.loading} skeleton={<Skeleton />}>
      <div className={'wrap' + (f.error ? ' stale' : '')}>
        {f.error && <StaleBanner what="buses" since={f.updatedAt} onRetry={f.retry} />}
        <div className="phead">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span className="mono">{dateLine}</span>
            <h1 className="h1">{headline}</h1>
            <p className="sub">{subline}</p>
          </div>
          {f.updatedAt && (
            <span className="sm" style={{ color: 'var(--faint)', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span className="livedot" />Live · updated {hhmm(f.updatedAt)}
            </span>
          )}
        </div>

        {f.buses.length === 0 ? (
          <section className="panel">
            <Empty icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="3" width="16" height="15" rx="3" /><path d="M4 11h16M8 21v-3M16 21v-3" /></svg>}
              title="Add your first bus" body="Add a bus and its driver. The driver signs in with a code, taps Start, and the bus appears on your map.">
              <button type="button" className="btn b-ink" onClick={openAddBus}>Add a bus</button>
              <a className="btn b-line" {...go('ConsoleSettings')}>Get a sign-up code</a>
            </Empty>
          </section>
        ) : (
          <div className="two" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.55fr) minmax(0, 1fr)', gap: 24, alignItems: 'start' }}>
            <section className="panel" aria-label="To do" data-zx="ov-todo">
              <div className="ph">
                <h2 className="h2">To do</h2>
                <span className="sm" style={{ color: 'var(--muted)' }}>{visible.length ? `${plural(visible.length, 'item')} · most urgent first` : 'All clear'}</span>
              </div>
              {visible.length === 0 && (
                <Empty title="Nothing needs you" body="New problems — expired papers, fuel that looks wrong, alerts — appear here as soon as they happen." />
              )}
              {todos.map((t) => (
                <div key={t.id} className={'todo dismiss' + (done.has(t.id) ? ' gone' : '')}>
                  <span className="sev" style={{ background: t.sev === 'bad' ? 'var(--bad)' : t.sev === 'warn' ? 'var(--warn)' : 'var(--faint)' }} />
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                      <Chip small tone={t.when === 'Today' ? 'bad' : t.when === 'This week' ? 'warn' : 'mute'}>{t.when}</Chip>
                      {t.zana && (
                        <span className="chip c-z" style={{ height: 22, fontSize: 12 }}>
                          <span aria-hidden="true" style={{ width: 14, height: 14, borderRadius: 4, background: 'var(--z)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 2, flex: 'none' }}>
                            <i style={{ width: 3, height: 4, borderRadius: 3, background: '#fff', display: 'block' }} /><i style={{ width: 3, height: 4, borderRadius: 3, background: '#fff', display: 'block' }} />
                          </span>Zana noticed
                        </span>
                      )}
                    </div>
                    <b style={{ fontSize: 16, lineHeight: '22px' }}>{t.title}</b>
                    <span className="sub sm" style={{ fontSize: 14, lineHeight: '20px' }}>{t.body}</span>
                    {t.plan && take && (
                      <button type="button" className="btn s b-ghost" style={{ color: 'var(--z-ink)', alignSelf: 'flex-start', marginLeft: -10, height: 30 }} onClick={() => take.start(t.plan as Plan)}
                        title="Zana shows her plan first and asks before sending anything">
                        <ZanaMark />Zana can do it
                      </button>
                    )}
                  </div>
                  {t.actions}
                </div>
              ))}
            </section>

            <section className="panel" aria-label={`This ${part}`} style={{ overflow: 'hidden' }}>
              <div className="ph">
                <h2 className="h2">This {part}</h2>
                <a className="b-ghost btn s" {...go('ConsoleLiveMap')}>Open map <IcArrow /></a>
              </div>
              <div style={{ height: 220, borderBottom: '1px solid var(--line)' }}>
                <MapView buses={f.buses} gate={f.org.gate} dark={dark} interactive={false} label="Where your buses are now" onSelect={() => undefined} />
              </div>
              <div style={{ padding: '6px 20px 12px' }}>
                <div className="row" style={{ justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--line)' }}>
                  <span>On route</span><b className="num">{onRoad.length} of {plural(f.buses.length, 'bus', 'buses')}</b>
                </div>
                <div className="row" style={{ justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--line)' }}>
                  <span>Children aboard</span><b className="num">{onRoad.length ? `${n0(aboard)} of ${n0(expectedOnRoad)}` : '—'}</b>
                </div>
                <div className="row" style={{ justifyContent: 'space-between', padding: '10px 0' }}>
                  <span>Running late</span>
                  {late.length ? (
                    <span className="row" style={{ gap: 8 }}><b>{late[0].name}</b>{late.length > 1 && <Chip tone="warn">+{late.length - 1} more</Chip>}</span>
                  ) : <span style={{ color: 'var(--faint)' }}>None</span>}
                </div>
              </div>
            </section>
          </div>
        )}

        {f.buses.length > 0 && (
          <div className="stats">
            <div className="stat" data-zx="ov-km">
              <span className="k">Kilometres, last 7 days</span>
              <StatQ p={pt.km} hover onAsk={ask} />
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <span className="big">{n0(km7)}</span>
                <Spark values={weekly(runsDone.map((r) => ({ at: r.date, v: r.km || 0 })))} tone={kmDelta != null && kmDelta < 0 ? 'muted' : 'ok'} />
              </div>
              <span className="sm">
                {kmDelta != null ? <span className={'delta ' + (kmDelta >= 0 ? 'up-g' : 'flat')}>{kmDelta >= 0 ? '+' : '−'}{Math.abs(kmDelta)}%</span> : null}
                <span style={{ color: 'var(--faint)' }}> {kmDelta != null ? 'vs the week before' : `${n0(km30)} km in 30 days`}</span>
              </span>
            </div>
            <div className="stat" data-zx="ov-fuel" title={`The cost of the refills drivers logged from 1 ${new Date(monthStart).toLocaleDateString('en-GB', { month: 'long' })} to today, against 1–${prevDays} ${prevName}. The line is each of the last 7 weeks.`}>
              <span className="k">Fuel this month<StatQ p={pt.fuel} onAsk={ask} /></span>
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <span className="big"><span className="unit">Ksh</span>{n0(fuelAnim)}</span>
                <Spark values={weekly(f.refills.map((r) => ({ at: r.at, v: r.amount || 0 })))} tone={notLogging ? 'muted' : fuelDelta != null && fuelDelta > 0 ? 'bad' : 'ok'} />
              </div>
              <span className="sm">
                {fuelDelta != null && <span className={'delta ' + (notLogging ? 'flat' : fuelDelta > 0 ? 'dn-r' : 'up-g')}>{fuelDelta > 0 ? '+' : '−'}{Math.abs(fuelDelta)}%</span>}
                <span style={{ color: 'var(--faint)' }}> {fuelDelta != null ? `vs 1–${prevDays} ${prevName}` : 'from refills drivers logged'}</span>
                {notLogging > 0 && <>
                  <span style={{ color: 'var(--faint)' }}> · </span>
                  <a className="delta dn-r" style={{ textDecoration: 'none' }} {...go('ConsoleFuel')}>{plural(notLogging, 'bus', 'buses')} not logging</a>
                </>}
              </span>
            </div>
            <div className="stat" data-zx="ov-kids">
              <span className="k">Children carried<StatQ p={pt.kids} onAsk={ask} /></span>
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <span className="big">{n0(carriedMonth)}</span>
                <Spark values={weekly(runsDone.map((r) => ({ at: r.date, v: r.served })))} tone="ok" />
              </div>
              <span className="sm"><span style={{ color: 'var(--faint)' }}>pickups and drop-offs this month</span></span>
            </div>
            <div className="stat" data-zx="ov-alerts">
              <span className="k">Open alerts</span>
              <StatQ p={pt.al} side="r" hover onAsk={ask} />
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <span className="big">{unread.length}</span>
                <Spark values={weekly(f.alerts.map((a) => ({ at: a.at, v: 1 })))} tone={unread.length ? 'bad' : 'ok'} />
              </div>
              <span className="sm">
                {urgent.count
                  ? <a className="delta dn-r" style={{ textDecoration: 'none' }} {...go('ConsoleAlerts', '?tab=urgent')}>{urgent.count} urgent</a>
                  : <span className="delta up-g">None urgent</span>}
                <span style={{ color: 'var(--faint)' }}> · {plural(f.alerts.filter((a) => a.at && now - a.at.getTime() < 7 * DAY).length, 'alert')} this week</span>
              </span>
            </div>
          </div>
        )}

        {f.buses.length > 0 && (
          <section className="panel" aria-label="Buses" data-zx="ov-buses">
            <div className="ph">
              <h2 className="h2">Buses right now</h2>
              <a className="btn s b-ghost" {...go('ConsoleBuses')}>All {plural(f.buses.length, 'bus', 'buses')} <IcArrow /></a>
            </div>
            <div className="tblbox">
              <table className="t">
                <thead><tr><th>Bus</th><th>Status</th><th>Aboard</th><th>Driver</th><th>Needs a look</th></tr></thead>
                <tbody>
                  {f.buses.slice(0, 10).map((b) => (
                    <tr key={b.id} onClick={() => navigate(`/fleet/buses?bus=${encodeURIComponent(b.id)}`)}>
                      <td><div className="who"><b>{b.name}</b>{plateBeside(b) && <span>{b.plate}</span>}</div></td>
                      <td><StatusChip status={b.status} label={b.statusLabel} /></td>
                      <td className="num">{b.aboard != null ? `${b.aboard} of ${b.expected ?? '—'}` : <span style={{ color: 'var(--faint)' }}>—</span>}</td>
                      <td>{b.driverName || <span style={{ color: 'var(--faint)' }}>No driver</span>}</td>
                      <td>{b.needsLook ? <Chip small tone={/expired|overdue/i.test(b.needsLook) ? 'bad' : 'warn'}>{b.needsLook}</Chip> : <span style={{ color: 'var(--faint)' }}>—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
        <p className="sm" style={{ margin: 0, color: 'var(--faint)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <ZanaMark /> Ask Zana about any number on this page. She reads your records and never changes anything without asking.
        </p>
      </div>
    </PageBody>
  );
};

export default Overview;
